import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { useState } from 'react';

import Slider from 'react-slick';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image, type StaticImageData } from 'Components/Image';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';
import { Video } from 'Components/Video';

import { BackgroundNextImage } from 'Blocks/BackgroundNextImage';

import { expertsModule } from 'assets/content/landingPages/skincare';
import playButton from 'assets/images/play.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Container = styled.div`
  background-color: ${theme.colors.neutral[300]};
  padding: 68px 0px;

  ${legacyTheme.breakpoints.up('sm')} {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    width: 100%;
    padding: 94px 120px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    padding: 86px 240px;
  }
`;

const HeaderContainer = styled.div`
  color: ${theme.colors.primary['300']};
  padding: 0 20px 0 32px;

  ${legacyTheme.breakpoints.up('sm')} {
    width: 100%;
    max-width: 800px;
  }
  ${legacyTheme.breakpoints.up('lg')} {
    width: 82%;
    max-width: 800px;
  }
`;

const Heading = styled(Typography)`
  font-size: 32px;
`;

const ExpertCardsDesktop = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  justify-content: space-between;

  ${legacyTheme.breakpoints.up('sm')} {
    grid-gap: 20px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    grid-gap: 40px;
    min-width: 800px;
  }
  ${legacyTheme.breakpoints.up('xl')} {
    max-width: 800px;
  }
`;

const ExpertCard = styled.div`
  flex: 1;
  padding-right: 25px;
  ${legacyTheme.breakpoints.up('sm')} {
    padding-right: 0;
  }
`;

const StyledSlider = styled(Slider)`
  display: flex;
`;

const InstagramHandle = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 25px;
  gap: 15px;
  width: 100%;

  ${legacyTheme.breakpoints.up('sm')} {
    gap: 5px;
  }
  ${legacyTheme.breakpoints.up('md')} {
    gap: 15px;
  }
`;

const InstagramHandleText = styled(Typography)`
  font-size: 18px;

  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 14px;
  }
  ${legacyTheme.breakpoints.up('md')} {
    font-size: 18px;
  }
`;

const PlayButton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const PausedScreenChildrenWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

interface ExpertsModuleProps {
  content?: {
    headlineDesktop: string;
    headlineMobile: string;
    subHeadline: string;
    experts: Array<{
      poster: StaticImageData;
      videoSrc: string;
      alt: string;
      name: string;
      description: string;
      instagramHandle: string;
      instagramThumbnail: StaticImageData;
      instagramThumbnailAlt: string;
    }>;
  };
}

export const ExpertsModule = ({ content = expertsModule }: ExpertsModuleProps) => {
  const { isMobile, isTablet } = useResponsiveVariant();

  const [currentPlaying, setCurrentPlaying] = useState<undefined | number>(undefined);

  const [currentSlide, setCurrentSlide] = useState(0);

  const expertCards = content.experts.map((expert, index) => (
    <ExpertCard key={expert.name}>
      <Video
        defaultControls
        forcePause={index !== currentPlaying}
        fullHeight
        fullWidth
        onPlay={() => (currentPlaying === index ? undefined : setCurrentPlaying(index))}
        overlay={{
          paused: (
            <BackgroundNextImage
              image={{
                mobile: expert.poster,
                tablet: expert.poster,
                desktop: expert.poster,
              }}
              imageAlt={expert.alt}
            >
              <PausedScreenChildrenWrapper>
                <InstagramHandle>
                  <Image
                    alt={expert.instagramThumbnailAlt}
                    height={isTablet ? 20 : 33}
                    src={expert.instagramThumbnail.src}
                    width={isTablet ? 20 : 33}
                  />

                  <InstagramHandleText align="center" color="white" variant="p1">
                    {expert.instagramHandle}
                  </InstagramHandleText>
                </InstagramHandle>
                <PlayButton>
                  <Image alt="play button" height={40} src={playButton} width={40} />
                </PlayButton>
              </PausedScreenChildrenWrapper>
            </BackgroundNextImage>
          ),
        }}
        posterImage={expert.poster}
        posterImageAlt={expert.alt}
        videoSource={expert.videoSrc}
      />
      <Spacer size={28} />

      <Typography align="center" bold color="vert" markupName="h3" upperCase variant="mono1">
        {expert.name}
      </Typography>

      <Typography align="center" color="vert" variant="p1" whiteSpace="pre-line">
        {expert.description}
      </Typography>
    </ExpertCard>
  ));

  return (
    <Container data-testid="skincare-lp-experts">
      <HeaderContainer>
        <Heading align={isMobile ? 'left' : 'center'} medium variant="h2" whiteSpace="pre-line">
          {isMobile ? content.headlineMobile : content.headlineDesktop}
        </Heading>
        <Spacer size={isMobile ? 12 : 24} />

        <Typography align={isMobile ? 'left' : 'center'} variant="p1">
          {content.subHeadline}
        </Typography>
      </HeaderContainer>
      <Spacer size={isMobile ? 32 : 52} />
      {isMobile ? (
        <StyledSlider
          arrows={false}
          beforeChange={(oldIndex, newIndex) => {
            if (newIndex === oldIndex) return;

            setCurrentSlide(newIndex > oldIndex ? currentSlide + 1 : currentSlide - 1);
            setCurrentPlaying(undefined);
          }}
          centerMode
          centerPadding="32px"
          dots={false}
          focusOnSelect
          infinite={false}
          slidesToShow={1.2}
        >
          {expertCards}
        </StyledSlider>
      ) : (
        <ExpertCardsDesktop>{expertCards}</ExpertCardsDesktop>
      )}
    </Container>
  );
};
